import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  Box,
  Heading,
  useToast,
  Button,
  FormControl,
  FormLabel,
  Input,
  useColorModeValue,
  Center,
  VStack,
  Text,
  Stack,
  SimpleGrid,
  Container,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Select,
  Checkbox,
} from "@chakra-ui/react";

import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

import { IField, CropType } from "../../types/fieldInterface";

import MapSelector2 from "../../components/MapSelector2";
import Navbar from "../../components/Navbar";
import useAxios from "../../utils/useAxios";
import { translateCropTypeName } from "../../utils/translateCropTypeName";
import SoilSettingsTab from "../../components/FieldComponents.tsx/SoilSettingsTab";
import MoistureLimitsTab from "../../components/FieldComponents.tsx/MoistureLimitsTab";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

const DEFAULT_MANAGEMENT_STRATEGY = "automatic";

const normalizeManagementStrategy = (strategy?: string) => {
  if (strategy === "soil_physics" || strategy === "automatic") {
    return strategy;
  }
  return DEFAULT_MANAGEMENT_STRATEGY;
};

const normalizeMostSignificantDepth = (depth?: number) => {
  if (depth === 1 || depth === 2 || depth === 3) {
    return depth;
  }
  return undefined;
};

const normalizeField = (incoming: IField): IField => ({
  ...incoming,
  management_feedback_strategy: normalizeManagementStrategy(incoming.management_feedback_strategy),
  most_significant_depth: normalizeMostSignificantDepth(incoming.most_significant_depth),
});

const AddModifyField = () => {
  const api = useAxios();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);

  const [field, setField] = useState<IField>({
    management_feedback_strategy: DEFAULT_MANAGEMENT_STRATEGY,
  });
  const [fieldName, setFieldName] = useState<string>("");
  const [fieldArea, setFieldArea] = useState<string>("");

  const [cropTypes, setCropTypes] = useState<CropType[]>([]);

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    // Load cached field data
    if (id) {
      const cached = localStorage.getItem(`field_${id}`);
      if (cached) {
        const data = JSON.parse(cached);
        setField(normalizeField(data));
        setFieldName(data.field_name);
        setFieldArea(data.area);
        setIsLoading(false);
      }
    }
    const fetchData = async () => {
      try {
        const response = await api.get(`agtech/fields/${id}/`);
        setField(normalizeField(response.data));
        // Cache field data
        localStorage.setItem(`field_${id}`, JSON.stringify(response.data));
        console.log(response.data);
        setFieldName(response.data.field_name);
        setFieldArea(response.data.area);
      } catch (error: any) {
        // Toast
        toast({
          title: <Trans>Error fetching data</Trans>,
          status: "error",
          description: error.response.data,
          duration: 9000,
          isClosable: true,
        });
      }
    };
    if (id) {
      fetchData();
    } else {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      if (fieldArea) {
        setIsLoading(false);
      }
    }
  }, [fieldArea]);

  useEffect(() => {
    const fetchCropTypes = async () => {
      try {
        const response = await api.get("agtech/crop-types/");
        console.log(response.data);
        setCropTypes(response.data);
      } catch (error: any) {
        // Toast
        toast({
          title: <Trans>Error fetching data</Trans>,
          status: "error",
          description: error.response.data,
          duration: 9000,
          isClosable: true,
        });
      }
    };
    fetchCropTypes();
  }, []);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // native form validation
    if (formRef.current && !formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return;
    }
    // Validate that fieldArea is set
    if (!fieldArea) {
      toast({
        title: <Trans>Field area not set</Trans>,
        description: <Trans>Please set the field area</Trans>,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }

    if (!field.crop_type) {
      toast({
        title: <Trans>Crop type is required</Trans>,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }

    const strategy = normalizeManagementStrategy(field.management_feedback_strategy);
    const mostSignificantDepth = normalizeMostSignificantDepth(field.most_significant_depth);

    if (!mostSignificantDepth) {
      toast({
        title: <Trans>Most significant depth is required</Trans>,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
      return;
    }

    const payload: Record<string, any> = {
      field_name: fieldName,
      crop_type: field.crop_type,
      area: fieldArea,
      management_feedback_strategy: strategy,
      most_significant_depth: mostSignificantDepth,
      download_satellite_data: field.download_satellite_data ?? false,
    };

    if (strategy === "soil_physics") {
      payload.soil_texture_sand = field.soil_texture_sand;
      payload.soil_texture_silt = field.soil_texture_silt;
      payload.soil_texture_clay = field.soil_texture_clay;
      payload.soil_texture_organic_matter = field.soil_texture_organic_matter;

      payload.moisture_limit_critical = field.moisture_limit_critical;
      payload.moisture_limit_damage = field.moisture_limit_damage;
      payload.moisture_limit_deficit = field.moisture_limit_deficit;
      payload.moisture_limit_field_capacity = field.moisture_limit_field_capacity;
    }

    try {
      if (field.id) {
        await api.put(`agtech/fields/${id}/`, payload);
      } else {
        await api.post(`agtech/fields/`, payload);
      }
      navigate("/fields");

      // Toast
      toast({
        title: <Trans>Data saved</Trans>,
        description: <Trans>Data has been saved successfully</Trans>,
        status: "success",
        duration: 9000,
        isClosable: true,
      });
    } catch (error: any) {
      console.log(error);
      // Toast
      toast({
        title: <Trans>Error saving data</Trans>,
        description: error.response.data,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const [tabIndex, setTabIndex] = useState(0);

  const selectedCropType = cropTypes.find((cropType) => cropType.id === field.crop_type);
  const fieldWithCropDetails = selectedCropType
    ? { ...field, crop_type_details: selectedCropType }
    : field;
  const isAutomaticMode =
    normalizeManagementStrategy(field.management_feedback_strategy) === "automatic";
  const maxTabIndex = isAutomaticMode ? 1 : 2;

  useEffect(() => {
    if (tabIndex > maxTabIndex) {
      setTabIndex(maxTabIndex);
    }
  }, [maxTabIndex, tabIndex]);

  const handleNext = () => {
    setTabIndex((prevIndex) => Math.min(prevIndex + 1, maxTabIndex));
  };

  const handlePrevious = () => {
    setTabIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  return (
    <Navbar>
      <Container
        bg={useColorModeValue("white", "gray.900")}
        p={4}
        borderRadius={8}
        boxShadow="md"
        mt={4}
        maxW="2xl"
      >
        <Box>
          <Heading as="h1" size="2xl" textAlign="center" mb={4}>
            {field.id ? <Trans>Modify Field</Trans> : <Trans>Add Field</Trans>}
          </Heading>

          <form ref={formRef} onSubmit={handleSave} noValidate>
            <VStack spacing={4} align="stretch">
              <FormControl id="field-name" isRequired>
                <FormLabel>
                  <Trans>Field Name</Trans>
                </FormLabel>
                <Input
                  required
                  type="text"
                  value={fieldName}
                  onChange={(e) => setFieldName(e.target.value)}
                  placeholder={t`e.g. "My Field"`}
                />
              </FormControl>
              <FormControl id="crop-type" isRequired>
                <FormLabel>
                  <Trans>Crop Type</Trans>
                </FormLabel>
                <Select
                  required
                  placeholder={t`Select crop type`}
                  value={field?.crop_type ?? ""}
                  onChange={(e) => {
                    setField({
                      ...field,
                      crop_type: Number(e.target.value),
                    });
                  }}
                >
                  {cropTypes.map((cropType) => (
                    <option key={cropType.id} value={cropType.id}>
                      {translateCropTypeName(cropType.name)}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl id="management-feedback-strategy" isRequired>
                <FormLabel>
                  <Trans>Management Feedback Strategy</Trans>
                </FormLabel>
                <Select
                  required
                  value={normalizeManagementStrategy(field.management_feedback_strategy)}
                  onChange={(e) => {
                    setField({
                      ...field,
                      management_feedback_strategy: e.target.value,
                    });
                  }}
                >
                  <option value="soil_physics">{t`Soil physics`}</option>
                  <option value="automatic">{t`Automatic`}</option>
                </Select>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  <Trans>
                    Soil physics lets you manually configure soil settings and moisture limits.
                    Automatic uses crop-based moisture index thresholds and hides manual soil setup.
                  </Trans>
                </Text>
              </FormControl>
              <Checkbox
                isChecked={field.download_satellite_data ?? false}
                onChange={(e) => setField({ ...field, download_satellite_data: e.target.checked })}
              >
                <Trans>Load historical satellite data and update daily</Trans>
              </Checkbox>
              <FormControl id="most-significant-depth" isRequired>
                <FormLabel>
                  <Trans>Most Significant Depth</Trans>
                </FormLabel>
                <Select
                  required
                  placeholder={t`Select depth`}
                  value={field.most_significant_depth ?? ""}
                  onChange={(e) => {
                    setField({
                      ...field,
                      most_significant_depth: Number(e.target.value),
                    });
                  }}
                >
                  <option value={1}>{t`Depth 1`}</option>
                  <option value={2}>{t`Depth 2`}</option>
                  <option value={3}>{t`Depth 3`}</option>
                </Select>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  <Trans>
                    Choose which sensor level should be prioritized for irrigation recommendations
                    and moisture-limit interpretation.
                  </Trans>
                </Text>
              </FormControl>
              <Tabs index={tabIndex} onChange={(index) => setTabIndex(index)}>
                <TabList>
                  <Tab>
                    <Trans>Field Area</Trans>
                  </Tab>
                  {!isAutomaticMode && (
                    <Tab>
                      <Trans>Soil Settings</Trans>
                    </Tab>
                  )}
                  <Tab>
                    <Trans>Moisture Limits</Trans>
                  </Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <FormLabel>
                      <Trans>Field Area</Trans>
                    </FormLabel>
                    <Text>
                      <Trans>Click on the map to draw the field area</Trans>
                    </Text>
                    {!isLoading ? (
                      <MapSelector2 features={fieldArea} setFeatures={setFieldArea} />
                    ) : (
                      <Center>
                        <Text>
                          <Trans>Loading...</Trans>
                        </Text>
                      </Center>
                    )}
                  </TabPanel>
                  {!isAutomaticMode && (
                    <TabPanel>
                      <SoilSettingsTab field={field} setField={setField} />
                    </TabPanel>
                  )}
                  <TabPanel>
                    <MoistureLimitsTab
                      field={fieldWithCropDetails}
                      setField={setField}
                      isAutomaticMode={isAutomaticMode}
                    />
                  </TabPanel>
                </TabPanels>
                <Button
                  onClick={handlePrevious}
                  isDisabled={tabIndex === 0}
                  leftIcon={<ChevronLeftIcon />}
                  mr={2}
                >
                  <Trans>Prev</Trans>
                </Button>
                <Button
                  onClick={handleNext}
                  isDisabled={tabIndex >= maxTabIndex}
                  rightIcon={<ChevronRightIcon />}
                  mr={2}
                >
                  <Trans>Next</Trans>
                </Button>
                <Button type="submit" colorScheme="teal" isDisabled={isLoading}>
                  <Trans>Save</Trans>
                </Button>
              </Tabs>
            </VStack>
          </form>
        </Box>
      </Container>
    </Navbar>
  );
};

export default AddModifyField;
